import { EProvince } from '@if/core/types'
import { QueryFunctionContext, useQuery } from '@tanstack/react-query'
import apiClient from './api-client'

const AMOUNT_FOR_FREQUENCY = 'amountForFrequency'
const AMOUNT_FOR_PROMOTIONAL_CREDIT_APP = 'amountForPromotionalCreditApp'

const keysFactory = {
  amountForFrequency: (computeParams: ComputeAmountForFrequencyDto | null) =>
    [{ scope: AMOUNT_FOR_FREQUENCY }, computeParams] as const,
  amountForPromotionalCreditApp: (computeFunding: ComputeFundingDto) =>
    [{ scope: AMOUNT_FOR_PROMOTIONAL_CREDIT_APP }, computeFunding] as const,
  estimateMonthlyPayment: (params: ComputeMonthlyAmountRequestDto | null) =>
    [{ scope: 'estimateMonthlyPayment' }, params] as const,
}

// -----------------------------------  computeAmountForFrequency  ------------------------
export interface ComputeAmountForFrequencyDto {
  merchantId: string
  paymentPlanId: string | null
  interestRate: number
  hasCoapplicant: boolean
  stateIso: EProvince
  paymentFrequency: string
  deliveryOn: string
  firstPaymentOn: string
  maxPmtAmount: number
  amountRequested: number
  includeInsurance: boolean
  include84Months: boolean
}
export interface ComputedAmountForFrequency {
  monthlyTerms: MonthlyPayment[]
}
export interface MonthlyPayment {
  term: number
  effectiveRateWithoutInsurance: number
  effectiveRateWithInsurance: number
  effectiveRateWithInsuranceIsLegal: boolean
  effectiveRateWithoutInsuranceIsLegal: boolean
  amountWithoutInsurance: number
  amountWithInsurance: number
  insuranceDelta: number
}

const computePaymentAmountForFrequency = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForFrequency']>>) => {
  const response = await apiClient.post('api/CreditApplication/computeAmountForFrequency', computeParams)
  return response.data as ComputedAmountForFrequency
}

export interface ComputeFundingResultDto {
  lenderFee: number
  lenderFeeRate: number
  totalInterestAmount: number
  insuranceFee: number
  insuranceTax: number
  paymentForFrequency: number
  effectiveRate: number
  requestId: string
  loanTerm: number
}

export interface ComputeFundingDto {
  creditApplicationId: string
  amountRequested: number
  firstPaymentOn: Date | null
  deliveryOn: string
  includeInsurance: boolean
  term: number
  paymentPlanId: string | null
}

export interface ComputeMonthTermFrequencyDto {
  creditApplicationId: string
  amountRequested: number
  firstPaymentOn: Date | null
  deliveryOn: string
  includeInsurance: boolean
  term: number
  paymentPlanId: string | null
}

export function useComputePaymentAmountForFrequency(
  computeParams: ComputeAmountForFrequencyDto | null,
): [ComputedAmountForFrequency | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.amountForFrequency(computeParams)],
    queryFn: computePaymentAmountForFrequency,
    enabled: computeParams !== null,
  })

  return [data ?? null, isFetching]
}

const getPromotionalCreditAppPlan = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForPromotionalCreditApp']>>) => {
  const response = await apiClient.post<ComputeFundingResultDto>(
    `api/CreditApplication/${encodeURIComponent(computeParams.creditApplicationId)}/computeFunding`,
    computeParams,
  )
  return response.data
}

export function useGetPromotionalCreditAppPlan(
  amountRequested: number,
  creditApplicationId: string,
  paymentPlanId: string | null,
): [ComputeFundingResultDto | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.amountForPromotionalCreditApp({
      amountRequested,
      creditApplicationId,
      paymentPlanId,
    } as ComputeFundingDto),
    queryFn: getPromotionalCreditAppPlan,
    enabled: !!creditApplicationId && Boolean(paymentPlanId),
  })

  return [data ?? null, isFetching]
}

export interface ComputeMonthlyAmountRequestDto {
  term: number
  amount: number
  minInterestRate: number
  maxInterestRate: number
  adminFeeRate: number
}

export interface ComputeEstimateMonthlyResultDto {
  minPaymentAmount: number
  maxPaymentAmount: number
}

const getEstimatedMonthlyPayment = async ({
  queryKey: [_, params],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['estimateMonthlyPayment']>>) => {
  const response = await apiClient.post<ComputeEstimateMonthlyResultDto>(
    'api/CreditApplication/EstimateMonthlyPayment',
    params,
  )
  return response.data
}

export function useEstimateMonthlyPayment(
  params: ComputeMonthlyAmountRequestDto | null,
): [ComputeEstimateMonthlyResultDto | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.estimateMonthlyPayment(params),
    queryFn: getEstimatedMonthlyPayment,
    enabled: params !== null,
  })

  return [data ?? null, isFetching]
}
